import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/constants/navigation'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import NewsletterForm from '@/components/landing/newsletter-form'
import RedRays from '@/components/landing/red-rays'

import { FOOTER_COLUMNS } from '@/constants/navigation'

const linkClass = 'group inline-flex items-center gap-1.5 rounded-sm text-[15px] leading-[21px] text-[#9c9ca0] transition-colors hover:text-white focus-visible:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/50'

const FooterLink: FC<{ link: NavLink }> = ({ link }): ReactNode => {
    if (link.href.startsWith('/')) {
        return (
            <Link href={link.href} className={linkClass}>
                {link.label}
            </Link>
        )
    }

    return (
        <a
            href={link.href}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
            className={linkClass}
        >
            {link.label}

            {link.external && (
                <>
                    <ArrowUpRight className='size-3 text-[#5d5e62] transition-colors group-hover:text-white' aria-hidden='true' />
                    <span className='sr-only'>
                        (opens in a new tab)
                    </span>
                </>
            )}
        </a>
    )
}

const SiteFooter: FC = (): ReactNode => {
    return (
        <>
            <RedRays />

            <footer className='relative border-t border-white/[0.08]'>
                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-x-0 top-0 h-[280px] bg-[radial-gradient(34%_100%_at_48%_0%,rgb(255_45_45/0.16),transparent)]'
                />

                <div className='relative mx-auto max-w-[1112px] px-6 pt-16 pb-16 sm:px-12 sm:pt-[104px] sm:pb-24'>
                    <nav aria-label='Footer' className='grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 lg:grid-cols-6'>
                        {FOOTER_COLUMNS.map(column => (
                            <div key={column.title}>
                                <h2 className='text-[15px] font-medium text-white'>
                                    {column.title}
                                </h2>

                                <ul className='mt-8 flex flex-col gap-4'>
                                    {column.links.map(link => (
                                        <li key={link.label}>
                                            <FooterLink link={link} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className='mt-20 grid gap-8 border-t border-white/[0.08] pt-12 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-16'>
                        <div className='max-w-[260px] pt-1'>
                            <h2 className='text-[15px] leading-[23px] font-normal text-white'>
                                Subscribe to our newsletter.
                            </h2>

                            <p className='text-[15px] leading-[23px] text-[#9c9ca0]'>
                                Get product updates and news in your inbox. No spam.
                            </p>
                        </div>

                        <NewsletterForm />
                    </div>
                </div>
            </footer>
        </>
    )
}

export default SiteFooter
